<template>
  <form id="arnSchritt1Form">
    <div class="my-4">
      <div class="bgTR px-4 px-lg-5 py-4 py-lg-5">
        <div class="schritt-zaehler-position">
          <SchritteZaehler class="w-100"></SchritteZaehler>
        </div>

        <!-- Versicherungsbeginn + RD-Nummer -->
        <div class="row mt-4 mb-3 w-100 align-items-center g-0">
          <div class="col-12 col-md-6 col-lg-3">
            <DateTextPickerField
              id="versbeginnArn"
              label="Versicherungsbeginn *"
              :modelValue="selectedVersicherungsbeginn"
              @update:modelValue="updateVersicherungsbeginn"
            />
          </div>

          <div class="col-12 col-md-6 d-flex justify-content-start justify-content-md-center">
            <RdnummerOk 
              v-if="shouldShowRdnummerInfo" 
              :rdnummer="store.aok" 
            />
          </div>
        </div>

        <!-- ==================== ARNS: Einzelversicherung ==================== -->
        <template v-if="isARNS">
          <div class="w-100 d-flex flex-column align-items-start mb-4 mt-4">
            <div class="d-flex flex-row">
              <div class="form-label font-xl-links-bold d-block mb-2">
                Zu versichernde Personen
              </div>
              <MinderjaehrigePersonPopover class="ms-2"></MinderjaehrigePersonPopover>
            </div>    
            <div class="font-base-links">
              Bitte ergänzen Sie folgende Daten
            </div>
          </div>

          <div v-for="(item, index) in store.items" :key="item.id" class="boxTR px-4 px-lg-5 py-3 py-lg-4 w-100 mb-4">
            <div class="row g-3 mb-3">
              <div class="col-12 col-md-3">
                <DateTextPickerField
                  :modelValue="item.geburtstag"
                  @update:modelValue="(val) => updateGeburtstag(item.id, val)"
                  :id="`vpGeburtstag${item.id}`"
                  label="Geburtsdatum *"
                  required
                />
              </div>
            </div>

            <!-- Hidden Checkbox für Tarif-Validierung (Tarif bereits in Schritt 0 gewählt) -->
            <div class="form-group d-none">
              <input
                type="checkbox"
                hidden
                class="form-check-input"
                :id="`vpTarif${item.id}`"
                :name="`vpTarif${item.id}`"
                v-model="item.hasTarif"
              >
            </div>

            <ArnPersonenKachel
              :item="item"
              :index="index"
              :ist-v-n="isItemVN(item.id)"
              @update:item="(newItem) => onItemUpdated(item.id, newItem)"
              @vn-changed="onVnChanged"
            />

            <div class="mt-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <button
                v-if="index === store.items.length - 1 && store.items.length < 10"
                type="button"
                class="font-base-links btn btn-vigo-1-500 rounded rounded-pill d-flex align-items-center text-nowrap"
                @click="personHinzufuegen"
              >
                <plus_icon class="me-3"></plus_icon>
                Weitere Person versichern
              </button>
              <PersonEntfernen v-if="store.items.length > 1" @click="personEntfernen(item.id)" />
            </div>
          </div>
        </template>

        <!-- ==================== ARNF: Familienversicherung ==================== -->
        <template v-if="isARNF">
          <div class="w-100 d-flex flex-column align-items-start mb-4 mt-4">
            <div class="form-label font-xl-links-bold d-block mb-2">
              Versicherungsnehmer
            </div>
            <div class="font-base-links">
              Bitte ergänzen Sie folgende Daten
            </div>
          </div>

          <!-- VN-Daten (erste und einzige Person bei ARNF) -->
          <div class="boxTR px-4 px-lg-5 py-3 py-lg-4 w-100 mb-4">
            <div class="row g-3 mb-3">
              <div class="col-12 col-md-3">
                <DateTextPickerField
                  :modelValue="firstItem.geburtstag"
                  @update:modelValue="(val) => updateGeburtstag(firstItem.id, val)"
                  :id="`vpGeburtstag${firstItem.id}`"
                  label="Geburtsdatum *"
                  required
                />
              </div>
            </div>

            <!-- Hidden Checkbox für Tarif-Validierung -->
            <div class="form-group d-none">
              <input
                type="checkbox"
                hidden
                class="form-check-input"
                :id="`vpTarif${firstItem.id}`"
                :name="`vpTarif${firstItem.id}`"
                v-model="firstItem.hasTarif"
              >
            </div>

            <ArnfPersonenKachel
              :item="firstItem"
              @update:item="(newItem) => onItemUpdated(firstItem.id, newItem)"
            />
          </div>

          <!-- Geburtsdatum der ältesten Person -->
          <div class="boxTR px-4 px-lg-5 py-3 py-lg-4 w-100 mb-4">
            <InfoGeburtsdatumAeltestePerson class="mb-3"></InfoGeburtsdatumAeltestePerson>
            <div class="row g-3">
              <div class="col-12 col-md-3">
                <DateTextPickerField
                  id="geburtsdatumAeltestePerson"
                  label="Geburtsdatum der ältesten Person *"
                  :modelValue="store.geburtsdatumAeltestePerson"
                  @update:modelValue="updateGeburtsdatumAeltestePerson"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- Berechnungskachel -->
        <div class="boxTR px-4 px-lg-5 py-3 py-lg-4 w-100 mt-4">
          <ArnBerechnungKachel 
            :items="store.items" 
            :vndaten="store.vndaten"
            :versicherungsbeginn="store.versicherungsbeginn"
            :geburtsdatumAeltestePerson="store.geburtsdatumAeltestePerson"
          />
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="d-flex justify-content-end gap-3 mt-4">
      <ZurueckButton @click="handleZurueck" />
      <WeiterButton @click="handleWeiter" />
    </div>
    <p class="text-end text-muted mt-2 font-sm-links">* Pflichtfeld</p>
  </form>
</template>

<script setup>
import { computed, nextTick, onMounted } from 'vue';
import { formValidation } from "@form-validation/bundle/popular";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import moment from "moment";

import { useRechnerStore } from '@/stores/rechner';
import { useMainStore } from '@/stores/useMainStore.js';
import { validatorAnrede, validatorTrimNotEmpty, validatorAeltestePerson, validatorGebHono, validatorKVNummer, validatorKVNummerSpecial, validatorKVNummerDoppelt } from '@/validators/validatoren';

import SchritteZaehler from '@/components/general/schritte_zaehler.vue';
import ArnPersonenKachel from '@/components/arn/arn_schritt1_personen_kachel.vue';
import ArnfPersonenKachel from '@/components/arn/arnf_schritt1_personen_kachel.vue';
import ArnBerechnungKachel from '@/components/arn/arn_berechnung_kachel.vue';
import MinderjaehrigePersonPopover from '@/components/ar20/ar20_minderjaehrige_person_versichern_popover.vue';
import InfoGeburtsdatumAeltestePerson from '@/components/arn/info_geburtsdatum_aelteste_person.vue';

import PersonEntfernen from '@/components/general/person_entfernen.vue';
import DateTextPickerField from '@/fields/DateTextPickerField.vue';
import plus_icon from '@/components/icons/plus_icon.vue';
import ZurueckButton from '@/components/general/zurueck_button.vue';
import WeiterButton from '@/components/general/weiter_button.vue';
import RdnummerOk from '@/components/general/rdnummer_ok.vue';

const store = useRechnerStore();
const mainStore = useMainStore();

// Tarif-Weiche: ARNS (tarif7) oder ARNF (tarif8)
const isARNS = computed(() => {
  const firstItem = store.items[0];
  return firstItem && firstItem.tarif7 === 1;
});

const isARNF = computed(() => {
  const firstItem = store.items[0];
  return firstItem && firstItem.tarif8 === 1;
});

// Erste Person (für ARNF ist das der einzige VN)
const firstItem = computed(() => store.items[0] || {});

// Prüft ob RD-Nummerninfo angezeigt werden soll
const shouldShowRdnummerInfo = computed(() => {
  return mainStore.aokEnabled && store.aok !== 0;
});

const isVigoselect = computed(() => mainStore.kanal === 'vigoselect');

const aoknummerValidators = {
  callback: {
    message: 'Bitte geben Sie eine gültige AOK Versicherten-Nr. ein.',
    callback: (input) => {
      if (store.aok !== 0) {
        return validatorKVNummerSpecial().validate(input).valid;
      }
      return validatorKVNummer().validate(input).valid;
    }
  },
  stringLength: {
    max: 10,
    message: 'Die AOK Versicherten-Nr. darf maximal 10 Zeichen lang sein.'
  },
  checkKVNummerDoppelt: {
    message: 'Die AOK Versicherten-Nr. darf nicht mehrfach verwendet werden.'
  }
};

// FormValidation Instanz
let fv = null;

// Versicherungsbeginn
const selectedVersicherungsbeginn = computed({
  get: () => store.versicherungsbeginn || '',
  set: (value) => {
    store.versicherungsbeginn = value;
    store.calculateAll();
  }
});

const updateVersicherungsbeginn = (value) => {
  store.versicherungsbeginn = value;
  store.calculateAll();
  if (fv) {
    nextTick(() => fv.revalidateField('versbeginnArn'));
  }
};

const updateGeburtsdatumAeltestePerson = (value) => {
  store.geburtsdatumAeltestePerson = value;
  // Bei ARNF: Berechnung basiert auf ältester Person
  store.calculateAll();
  if (fv) {
    nextTick(() => {
      fv.revalidateField('geburtsdatumAeltestePerson');
      // Bei ARNF: Auch VN-Geburtsdatum neu validieren (Cross-Validation)
      const firstItemId = store.items[0]?.id;
      if (firstItemId) {
        fv.revalidateField(`vpGeburtstag${firstItemId}`);
      }
    });
  }
};

const isItemVN = (itemId) => {
  return store.vndaten.id === itemId;
};

// Geburtsdatum-Validierung
const maxDateGeburt = moment();
const minDateGeburt = moment().subtract(105, 'years');
// Basis: ohne 18-Jahre-Prüfung (für ARNS-VPs, die nicht zwingend volljährig sein müssen)
const geburtstagsValidationBasis = {
  notEmpty: {
    message: 'Bitte wählen Sie ein Geburtsdatum aus.',
  },
  date: {
    format: 'YYYY-MM-DD',
    max: maxDateGeburt.format('YYYY-MM-DD'),
    min: minDateGeburt.format('YYYY-MM-DD'),
    message: `Das Datum muss zwischen ${minDateGeburt.format('DD.MM.YYYY')} und ${maxDateGeburt.format('DD.MM.YYYY')} liegen.`,
  },
};
// Mit 18-Jahre-Prüfung: nur für ARNF (VN = einzige VP, muss volljährig sein)
const geburtstagsValidation = {
  ...geburtstagsValidationBasis,
  checkGebHono: {
    message: 'Sie müssen mindestens 18 Jahre alt sein.',
  }
};

// Versicherungsbeginn-Validierung (heute bis max 6 Monate Zukunft)
const minDateVersbeginn = moment().startOf('day');
const maxDateVersbeginn = moment().add(6, 'months').startOf('day');
const versicherungsbeginnValidation = {
  notEmpty: {
    message: 'Bitte wählen Sie einen Versicherungsbeginn aus.',
  },
  date: {
    format: 'YYYY-MM-DD',
    min: minDateVersbeginn.format('YYYY-MM-DD'),
    max: maxDateVersbeginn.format('YYYY-MM-DD'),
    message: `Das Datum muss zwischen ${minDateVersbeginn.format('DD.MM.YYYY')} und ${maxDateVersbeginn.format('DD.MM.YYYY')} liegen.`,
  },
};

// Validierungsregeln pro Person generieren (ARNS)
function generateItemValidationRulesARNS(item) {
  const rules = {
    [`vpTarif${item.id}`]: {
      validators: {
        callback: {
          message: 'Bitte wählen Sie den Tarif aus.',
          callback: () => store.isTarifValid(item.id)
        }
      }
    },
    [`vpGeburtstag${item.id}`]: {
      validators: geburtstagsValidationBasis
    },
    [`vpAnrede${item.id}`]: {
      validators: {
        checkAnrede: {
          message: 'Bitte wählen Sie eine Anrede aus.',
        },
      },
    },
    [`vpVorname${item.id}`]: {
      validators: {
        checkTrimNotEmpty: {
          message: 'Bitte einen Vornamen angeben.',
        },
        stringLength: {
          max: 100,
          message: 'Der Vorname darf maximal 100 Zeichen lang sein.',
        },
      },
    },
    [`vpNachname${item.id}`]: {
      validators: {
        checkTrimNotEmpty: {
          message: 'Bitte einen Nachnamen angeben.',
        },
        stringLength: {
          max: 100,
          message: 'Der Nachname darf maximal 100 Zeichen lang sein.',
        },
      },
    },
    [`vpIstVN${item.id}`]: {
      validators: {
        notEmpty: {
          message: 'Bitte wählen Sie aus, ob diese Person der Antragsteller ist.',
        },
      },
    },
  };

  if (isVigoselect.value) {
    rules[`vpAoknummer${item.id}`] = {
      validators: aoknummerValidators
    };
  }

  return rules;
}

// Validierungsregeln für ARNF (nur eine Person, kein VN-Feld)
function generateItemValidationRulesARNF(item) {
  const rules = {
    [`vpTarif${item.id}`]: {
      validators: {
        callback: {
          message: 'Bitte wählen Sie den Tarif aus.',
          callback: () => store.isTarifValid(item.id)
        }
      }
    },
    [`vpGeburtstag${item.id}`]: {
      validators: geburtstagsValidation
    },
    [`vpAnrede${item.id}`]: {
      validators: {
        checkAnrede: {
          message: 'Bitte wählen Sie eine Anrede aus.',
        },
      },
    },
    [`vpVorname${item.id}`]: {
      validators: {
        checkTrimNotEmpty: {
          message: 'Bitte einen Vornamen angeben.',
        },
        stringLength: {
          max: 100,
          message: 'Der Vorname darf maximal 100 Zeichen lang sein.',
        },
      },
    },
    [`vpNachname${item.id}`]: {
      validators: {
        checkTrimNotEmpty: {
          message: 'Bitte einen Nachnamen angeben.',
        },
        stringLength: {
          max: 100,
          message: 'Der Nachname darf maximal 100 Zeichen lang sein.',
        },
      },
    },
  };

  if (isVigoselect.value) {
    rules[`vpAoknummer${item.id}`] = {
      validators: aoknummerValidators
    };
  }

  return rules;
}

// Validierung für neue Person hinzufügen (nur ARNS)
function addItemValidation(id) {
  if (!fv) return;
  
  addFieldIfExists(`vpTarif${id}`, {
    validators: {
      callback: {
        message: 'Bitte wählen Sie den Tarif aus.',
        callback: () => store.isTarifValid(id)
      }
    }
  });

  addFieldIfExists(`vpGeburtstag${id}`, {
    validators: geburtstagsValidationBasis
  });

  addFieldIfExists(`vpAnrede${id}`, {
    validators: {
      checkAnrede: {
        message: 'Bitte wählen Sie eine Anrede aus.',
      },
    }
  });

  addFieldIfExists(`vpVorname${id}`, {
    validators: {
      checkTrimNotEmpty: {
        message: 'Bitte einen Vornamen angeben.',
      },
      stringLength: {
        max: 100,
        message: 'Der Vorname darf maximal 100 Zeichen lang sein.',
      },
    }
  });

  addFieldIfExists(`vpNachname${id}`, {
    validators: {
      checkTrimNotEmpty: {
        message: 'Bitte einen Nachnamen angeben.',
      },
      stringLength: {
        max: 100,
        message: 'Der Nachname darf maximal 100 Zeichen lang sein.',
      },
    }
  });

  if (isVigoselect.value) {
    addFieldIfExists(`vpAoknummer${id}`, {
      validators: aoknummerValidators
    });
  }

  addFieldIfExists(`vpIstVN${id}`, {
    validators: {
      notEmpty: {
        message: 'Bitte wählen Sie aus, ob diese Person der Antragsteller ist.',
      },
    }
  });
}

// Validierung für Person entfernen
function removeItemValidation(id) {
  removeFieldIfExists(`vpTarif${id}`);
  removeFieldIfExists(`vpGeburtstag${id}`);
  removeFieldIfExists(`vpAnrede${id}`);
  removeFieldIfExists(`vpVorname${id}`);
  removeFieldIfExists(`vpNachname${id}`);
  removeFieldIfExists(`vpAoknummer${id}`);
  removeFieldIfExists(`vpIstVN${id}`);
}

// Item wurde aktualisiert
const onItemUpdated = (itemId, newItem) => {
  store.setItem(itemId, newItem);
};

const updateGeburtstag = (itemId, value) => {
  const item = store.items.find(i => i.id === itemId);
  if (item) {
    item.geburtstag = value;
    store.setItem(itemId, item);
    store.calculateAll();
    if (fv) {
      fv.revalidateField(`vpGeburtstag${itemId}`);
      // Bei ARNF: Auch Geburtsdatum der ältesten Person neu validieren (Cross-Validation)
      if (isARNF.value) {
        fv.revalidateField('geburtsdatumAeltestePerson');
      }
    }
  }
};

const addFieldIfExists = (fieldName, config) => {
  if (!fv) return;
  const element = document.getElementById(fieldName);
  if (!element) return;
  fv.addField(fieldName, config);
};

const removeFieldIfExists = (fieldName) => {
  if (!fv) return;
  try {
    const field = fv.getFields()[fieldName];
    if (field) {
      fv.removeField(fieldName);
    }
  } catch (error) {
    // Feld existiert nicht, ignorieren
  }
};

// VN-Auswahl geändert (nur ARNS)
const onVnChanged = ({ itemId, isVN }) => {
  const item = store.items.find(i => i.id === itemId);
  if (!item) return;
  if (isVN) {
    store.setVn(item);
  } else if (store.vndaten.id === itemId) {
    store.setVn(null);
  }
};

// Fügt eine weitere Person hinzu (nur ARNS, max 10)
const personHinzufuegen = () => {
  if (store.items.length < 10) {
    store.addItemBasedOnLast();
    const newItem = store.items.at(-1);
    // ARNS automatisch für neue Person aktivieren
    newItem.tarif7 = 1;
    newItem.hasTarif = true;
    store.calculateAll();
    nextTick(() => {
      addItemValidation(newItem.id);
    });
  }
};

// Person entfernen (nur ARNS)
const personEntfernen = (itemId) => {
  store.deleteItem(itemId);
  // Wenn die entfernte Person VN war, VN zurücksetzen
  if (store.vndaten.id === itemId) {
    store.setVn(null);
  }
  nextTick(() => {
    removeItemValidation(itemId);
  });
};

// Navigation: Zurück zu Schritt 0
const handleZurueck = () => {
  store.stepStatus = 0;
};

// Navigation: Weiter zu Schritt 2
const handleWeiter = () => {
  if (fv) {
    const button = document.querySelector('[data-shake]');
    fv.validate().then((status) => {
      if (status === 'Valid') {
        // Bei ARNS: VN-Daten finalisieren
        if (isARNS.value) {
          const vnItem = store.items.find(item => item.id === store.vndaten.id);
          if (vnItem) {
            store.setVn(vnItem);
          }
        }
        // Bei ARNF: Die einzige Person ist automatisch VN
        if (isARNF.value) {
          store.setVn(store.items[0]);
        }
        
        store.stepStatus = 2;
      } else {
        if (button) {
          button.classList.add('shake');
          setTimeout(() => button.classList.remove('shake'), 500);
        }
        nextTick(() => {
          const firstInvalid = document.querySelector('#arnSchritt1Form .is-invalid');
          if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    });
  }
};

onMounted(async () => {
  store.initializeStore();
  
  await nextTick();

  const fields = {
    versbeginnArn: {
      validators: versicherungsbeginnValidation
    },
  };
  
  // ARNS: Validierung für alle Personen
  if (isARNS.value) {
    store.items.forEach(item => {
      const rules = generateItemValidationRulesARNS(item);
      Object.assign(fields, rules);
    });
  }
  
  // ARNF: Validierung nur für VN + Geburtsdatum älteste Person
  if (isARNF.value) {
    const item = store.items[0];
    if (item) {
      const rules = generateItemValidationRulesARNF(item);
      Object.assign(fields, rules);
    }
    
    // Geburtsdatum der ältesten Person mit zusätzlicher Prüfung
    fields.geburtsdatumAeltestePerson = {
      validators: {
        ...geburtstagsValidation,
        checkAeltestePerson: {
          message: 'Das Geburtsdatum der ältesten Person darf nicht nach dem Geburtsdatum des Versicherungsnehmers liegen.',
        },
      }
    };
  }
  
  const form = document.getElementById('arnSchritt1Form');
  if (!form) return;
  
  fv = formValidation(form, {
    fields,
    plugins: {
      bootstrap5: new Bootstrap5({
        rowSelector: '.form-group, .form-check, .form-check-inline'
      }),
      submitButton: new SubmitButton(),
      trigger: new Trigger({
        event: 'blur change',
      }),
    },
  })
  .registerValidator('checkAnrede', validatorAnrede)
  .registerValidator('checkTrimNotEmpty', validatorTrimNotEmpty)
  .registerValidator('checkAeltestePerson', validatorAeltestePerson)
  .registerValidator('checkGebHono', validatorGebHono)
  .registerValidator('checkKVNummerDoppelt', validatorKVNummerDoppelt);
});
</script>

<style scoped>
.introclaim {
  margin-bottom: 1.5rem;
}

.schritt-zaehler-position {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
}

@media (min-width: 768px) {
  .schritt-zaehler-position {
    width: 80vw;
  }
}

@media (min-width: 992px) {
  .schritt-zaehler-position {
    width: 70vw;
  }
}

@media (min-width: 1200px) {
  .schritt-zaehler-position {
    width: 60vw;
  }
}

.font-xl-links-bold {
  font-weight: 700;
}

/* Shake Animation für ungültige Formulare */
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

.shake {
  animation: shake 0.5s ease-in-out;
}
</style>
